import { __awaiter } from "tslib";
import { Robot } from './objects/Robot';
import { Color, Scene } from 'three';
import { intsBetween } from '../utils/ints-between';
import { LevelMap } from './LevelMap';
import { defaultRobots, negligible } from '../utils/defines';
import { delete3dChildren } from '../utils/delete-3d-children';
import { mapObject } from '../utils/object-tools';
import { clearObject } from '../utils/object-tools';
import { currentLevelWin } from '../stores/current-level';
import { isUpdater } from './components/Updater';
import './debugger';
const stepsPerMillisecond = 3 / 1000;
/**
 * World - Represents the game world. Manages robots, tracks the current level and manages
 * transitions between levels. Is the primary object passed to children to understand their
 * context.
 */
export class World extends EventTarget {
    constructor() {
        super();
        this.boundTick = this.tick.bind(this);
        this.ticking = false;
        this.lastTick = 0;
        this.step = 0;
        this.runToStep = 0;
        this.robots = {};
        this.scene = new Scene();
        this.scene.background = new Color(0x111111);
        this.currentLevel = undefined;
        this.updaters = [];
        this.served = [];
    }
    startLevel(spec) {
        this.currentLevel = spec;
        this.resetLevel();
    }
    resetLevel() {
        // Clean up.
        delete3dChildren(this.scene);
        clearObject(this.robots);
        this.updaters = [];
        this.step = 0;
        this.ticking = false;
        this.runToStep = 0;
        this.robots = {};
        this.served = [];
        currentLevelWin.set(undefined);
        // Create a new level map.
        this.levelMap = new LevelMap(this, this.currentLevel);
        this.startStep(0);
    }
    getScene() {
        return this.scene;
    }
    getCodeLineCount() {
        return this.code.trim().split('\n').length;
    }
    remove(obj) {
        this.scene.remove(obj.model);
        this.updaters = this.updaters.filter(updater => updater !== obj);
        if (obj instanceof Robot) {
            delete this.robots[obj.name];
        }
    }
    add(obj) {
        this.scene.add(obj.model);
        if (isUpdater(obj)) {
            this.updaters.push(obj);
        }
        if (obj instanceof Robot) {
            if (obj.name === undefined) {
                const { name, hat } = defaultRobots[Object.keys(this.robots).length];
                obj.name = name;
                obj.setHatColor(new Color(hat));
            }
            this.robots[obj.name] = obj;
        }
    }
    run(steps) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                this.startTicking(steps, resolve);
            });
        });
    }
    runAll() {
        return __awaiter(this, void 0, void 0, function* () {
            while (Object.values(this.robots).some(r => r.isActive())) {
                yield this.run(1);
            }
        });
    }
    startTicking(steps, stopCallback) {
        if (this.stopCallback) {
            throw new Error("Can't startTicking while already ticking!");
        }
        this.runToStep += steps;
        this.stopCallback = stopCallback;
        this.startStep(this.step);
        this.ticking = true;
        this.lastTick = Date.now();
        requestAnimationFrame(this.boundTick);
    }
    checkEndCondition() {
        if (currentLevelWin.get() !== undefined) {
            return;
        }
        const result = this.currentLevel.testGoal(this);
        currentLevelWin.set(result);
        return result !== undefined;
    }
    onServed(delivered) {
        this.served.push(delivered);
        this.checkEndCondition();
    }
    stopTicking() {
        if (this.stopCallback) {
            this.stopCallback();
        }
        this.runToStep = this.step;
        this.ticking = false;
        this.stopCallback = undefined;
    }
    getStep() {
        return this.step;
    }
    /**
     * Returns the robots touching goals.
     */
    robotsAtGoals() {
        return Object.values(this.robots).filter(robot => this.levelMap.isGoal(robot.tilePos));
    }
    startStep(step) {
        for (const updater of this.updaters) {
            updater.startStep(step);
        }
    }
    endStep(step) {
        for (const updater of this.updaters) {
            updater.endStep(step);
        }
        this.checkEndCondition();
    }
    tick() {
        if (!this.ticking) {
            return;
        }
        // Figure out tick duration.
        const now = Date.now();
        const duration = now - this.lastTick;
        this.lastTick = now;
        // Figure out how many steps to run.
        const targetStep = Math.min(this.step + duration * stepsPerMillisecond, this.runToStep);
        // Tell all robots about the end and start of every step threshold we cross.
        for (const step of intsBetween(this.step, targetStep)) {
            this.endStep(step - 1);
            // Do not tell the robots to start the next step if we're stopping execution.
            if (step < this.runToStep) {
                this.startStep(step);
            }
        }
        // Update all robots to the current fractional step.
        this.step = targetStep;
        for (const updater of this.updaters) {
            updater.update(this.step, duration);
        }
        // Detect the end.
        if (Math.abs(this.step - this.runToStep) < negligible) {
            this.stopTicking();
        }
        else {
            requestAnimationFrame(this.boundTick);
        }
    }
    /**
     * Returns an API friendly JSON representation of the current world state.
     */
    toApiJson() {
        return {
            step: this.step,
            robots: mapObject(this.robots, robot => robot.toApiJson()),
            map: this.levelMap ? this.levelMap.toApiJson() : null,
        };
    }
}
